import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SearchComponent from '../../components/ui/SearchComponent';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StudentCard from './components/StudentCard';
import TeamInvitationCard from './components/TeamInvitationCard';
import CurrentTeamCard from './components/CurrentTeamCard';
import TeamBuilderModal from './components/TeamBuilderModal';
import FilterPanel from './components/FilterPanel';

const TeamFormationHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('find-teammates');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isTeamBuilderOpen, setIsTeamBuilderOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock current user data
  const currentUser = {
    id: 'user-1',
    name: 'Alex Johnson',
    role: 'Developer',
    university: 'Howard University'
  };

  // Mock students data
  const mockStudents = [
    {
      id: 'student-1',
      name: 'Sarah Williams',
      role: 'Project Manager',
      university: 'Spelman College',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
      experienceLevel: 'Intermediate',
      skills: ['Agile', 'Scrum', 'Leadership', 'Communication', 'Risk Management'],
      interests: 'Smart transportation systems, urban mobility solutions, and sustainable city planning initiatives.',
      availability: 'weekdays',
      location: 'Atlanta, GA',
      isOnline: true,
      hasInvitation: false
    },
    {
      id: 'student-2',
      name: 'Marcus Thompson',
      role: 'Business Analyst',
      university: 'Morehouse College',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
      experienceLevel: 'Advanced',
      skills: ['Data Analysis', 'Requirements Gathering', 'Process Modeling', 'SQL', 'Tableau'],
      interests: 'Urban data analytics, smart governance solutions, and citizen engagement platforms.',
      availability: 'flexible',
      location: 'Atlanta, GA',
      isOnline: false,
      hasInvitation: false
    },
    {
      id: 'student-3',
      name: 'Jasmine Davis',
      role: 'Product Designer',
      university: 'North Carolina A&T',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
      experienceLevel: 'Intermediate',
      skills: ['UI/UX Design', 'Figma', 'User Research', 'Prototyping', 'Design Systems'],
      interests: 'Accessible design for smart city interfaces, inclusive technology solutions.',
      availability: 'evenings',
      location: 'Greensboro, NC',
      isOnline: true,
      hasInvitation: false
    },
    {
      id: 'student-4',
      name: 'David Rodriguez',
      role: 'Developer',
      university: 'Florida A&M University',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
      experienceLevel: 'Advanced',
      skills: ['React', 'Node.js', 'Python', 'MongoDB', 'AWS', 'IoT Development'],
      interests: 'IoT solutions for smart cities, environmental monitoring systems, and data visualization.',
      availability: 'weekends',
      location: 'Tallahassee, FL',
      isOnline: true,
      hasInvitation: false
    },
    {
      id: 'student-5',
      name: 'Aisha Patel',
      role: 'Project Manager',
      university: 'Howard University',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150',
      experienceLevel: 'Beginner',
      skills: ['Project Planning', 'Team Coordination', 'Documentation', 'Stakeholder Management'],
      interests: 'Community-driven smart city projects, public-private partnerships in urban development.',
      availability: 'weekdays',
      location: 'Washington, DC',
      isOnline: false,
      hasInvitation: true
    },
    {
      id: 'student-6',
      name: 'Jordan Mitchell',
      role: 'Business Analyst',
      university: 'Spelman College',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150',
      experienceLevel: 'Intermediate',
      skills: ['Market Research', 'Financial Analysis', 'Strategic Planning', 'Excel', 'PowerBI'],
      interests: 'Economic impact of smart city initiatives, sustainable urban development models.',
      availability: 'flexible',
      location: 'Atlanta, GA',
      isOnline: true,
      hasInvitation: false
    }
  ];

  // Mock team invitations
  const mockInvitations = [
    {
      id: 'invite-1',
      sender: {
        id: 'student-7',
        name: 'Maya Johnson',
        role: 'Project Manager',
        avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150'
      },
      message: `Hi Alex! I'm forming a team to work on the Smart Traffic Management problem from Atlanta. Your development skills would be perfect for our IoT solution. Interested in joining us?`,
      teamName: 'Urban Flow Solutions',problemStatement: 'Develop an intelligent traffic management system to reduce congestion and improve urban mobility in Atlanta metropolitan area.',
      timestamp: new Date(Date.now() - 900000) // 15 minutes ago
    },
    {
      id: 'invite-2',
      sender: {
        id: 'student-8',name: 'Carlos Rivera',role: 'Business Analyst',avatar: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=150'
      },
      message: `Hey Alex! We're looking for a developer to join our team working on sustainable energy solutions. Your technical background would be valuable for our project.`,
      teamName: 'Green City Innovators',
      problemStatement: 'Create a comprehensive energy management platform for optimizing renewable energy distribution in smart city infrastructure.',
      timestamp: new Date(Date.now() - 3600000) // 1 hour ago
    }
  ];

  // Mock current team
  const mockCurrentTeam = {
    id: 'team-1',
    name: 'Smart Infrastructure Team',
    description: 'Developing innovative solutions for urban infrastructure challenges using IoT and data analytics.',
    leaderId: 'user-1',
    members: [
      {
        id: 'user-1',
        name: 'Alex Johnson',
        role: 'Developer',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150',
        isOnline: true
      },
      {
        id: 'student-9',
        name: 'Lisa Chen',
        role: 'Product Designer',
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150',
        isOnline: false
      },
      {
        id: 'student-10',
        name: 'Robert Kim',
        role: 'Business Analyst',
        avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150',
        isOnline: true
      }
    ],
    problemStatement: 'Design and implement a comprehensive smart parking solution that integrates with existing city infrastructure to optimize parking availability and reduce urban congestion.',
    stats: {
      tasksCompleted: 12,
      meetingsHeld: 8,
      daysActive: 15
    }
  };

  // Mock problem statements for team builder
  const mockProblemStatements = [
    {
      id: 'problem-1',
      title: 'Smart Traffic Management System',
      organization: 'City of Atlanta'
    },
    {
      id: 'problem-2',
      title: 'Sustainable Energy Distribution Platform',
      organization: 'City of Charlotte'
    },
    {
      id: 'problem-3',
      title: 'Citizen Engagement Mobile App',
      organization: 'City of Miami'
    }
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/student-dashboard' },
    { label: 'Team Formation Hub' }
  ];

  const tabs = [
    { id: 'find-teammates', label: 'Find Teammates', icon: 'Users', count: mockStudents?.length },
    { id: 'invitations', label: 'Invitations', icon: 'Mail', count: mockInvitations?.length },
    { id: 'current-team', label: 'Current Team', icon: 'Shield', count: mockCurrentTeam ? 1 : 0 }
  ];

  // Filter students based on search and filters
  const filteredStudents = mockStudents?.filter(student => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      const matchesSearch = 
        student?.name?.toLowerCase()?.includes(query) ||
        student?.role?.toLowerCase()?.includes(query) ||
        student?.university?.toLowerCase()?.includes(query) ||
        student?.skills?.some(skill => skill?.toLowerCase()?.includes(query)) ||
        student?.interests?.toLowerCase()?.includes(query);
      
      if (!matchesSearch) return false;
    }

    // Role filter
    if (filters?.role && filters?.role !== 'all' && student?.role !== filters?.role) {
      return false;
    }

    // Experience filter
    if (filters?.experience && filters?.experience !== 'all' && student?.experienceLevel !== filters?.experience) {
      return false;
    }

    // Availability filter
    if (filters?.availability && filters?.availability !== 'all' && student?.availability !== filters?.availability) {
      return false;
    }

    // University filter
    if (filters?.university && filters?.university !== 'all' && student?.university !== filters?.university) {
      return false;
    }

    // Skills filter
    if (filters?.skills && filters?.skills?.trim()) {
      const skillQuery = filters?.skills?.toLowerCase();
      const hasSkill = student?.skills?.some(skill => skill?.toLowerCase()?.includes(skillQuery));
      if (!hasSkill) return false;
    }

    // Online filter
    if (filters?.onlineOnly && !student?.isOnline) {
      return false;
    }

    return true;
  });

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleInviteStudent = async (studentId) => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Update student invitation status
    const updatedStudents = mockStudents?.map(student => 
      student?.id === studentId ? { ...student, hasInvitation: true } : student
    );
    
    setIsLoading(false);
    console.log('Invited student:', studentId);
  };

  const handleViewProfile = (studentId) => {
    console.log('View profile:', studentId);
    // Navigate to student profile or open modal
  };

  const handleAcceptInvitation = async (invitationId) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Accepted invitation:', invitationId);
    setIsLoading(false);
  };

  const handleDeclineInvitation = async (invitationId) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Declined invitation:', invitationId);
    setIsLoading(false);
  };

  const handleMessageUser = (userId) => {
    navigate('/communication-hub', { state: { userId } });
  };

  const handleCreateTeam = async (teamData) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Created team:', teamData);
    setIsLoading(false);
  };

  const handleLeaveTeam = async (teamId) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Left team:', teamId);
    setIsLoading(false);
  };

  const handleManageTeam = (teamId) => {
    console.log('Manage team:', teamId);
    // Open team management modal or navigate
  };

  const handleViewWorkspace = (teamId) => {
    navigate('/team-workspace', { state: { teamId } });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const renderFindTeammates = () => (
    <div className="space-y-6">
      {/* Search and Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <SearchComponent
            placeholder="Search students by name, skills, or interests..."
            onSearch={handleSearch}
            onClear={handleClearSearch}
            searchScope="users"
          />
        </div>
        <Button
          variant="default"
          onClick={() => setIsTeamBuilderOpen(true)}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Create Team
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filter Panel */}
        <div className="lg:col-span-1">
          <FilterPanel
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
          />
        </div>

        {/* Students Grid */}
        <div className="lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredStudents?.length} of {mockStudents?.length} students
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              iconName="Filter"
              iconPosition="left"
              iconSize={14}
              className="lg:hidden"
            >
              Filters
            </Button>
          </div>

          {filteredStudents?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredStudents?.map((student) => (
                <StudentCard
                  key={student?.id}
                  student={student}
                  onInvite={handleInviteStudent}
                  onViewProfile={handleViewProfile}
                  currentUserId={currentUser?.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No students found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search criteria or filters to find teammates.
              </p>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderInvitations = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Team Invitations</h2>
        <span className="text-sm text-muted-foreground">
          {mockInvitations?.length} pending invitation{mockInvitations?.length !== 1 ? 's' : ''}
        </span>
      </div>

      {mockInvitations?.length > 0 ? (
        <div className="space-y-4">
          {mockInvitations?.map((invitation) => (
            <TeamInvitationCard
              key={invitation?.id}
              invitation={invitation}
              onAccept={handleAcceptInvitation}
              onDecline={handleDeclineInvitation}
              onMessage={handleMessageUser}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon name="Mail" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No invitations</h3>
          <p className="text-muted-foreground">
            You don't have any pending team invitations at the moment.
          </p>
        </div>
      )}
    </div>
  );

  const renderCurrentTeam = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Current Team</h2>
        {mockCurrentTeam && (
          <Button
            variant="outline"
            onClick={() => handleViewWorkspace(mockCurrentTeam?.id)}
            iconName="ExternalLink"
            iconPosition="left"
            iconSize={14}
          >
            Open Workspace
          </Button>
        )}
      </div>

      {mockCurrentTeam ? (
        <CurrentTeamCard
          team={mockCurrentTeam}
          onLeaveTeam={handleLeaveTeam}
          onManageTeam={handleManageTeam}
          onViewWorkspace={handleViewWorkspace}
          currentUserId={currentUser?.id}
        />
      ) : (
        <div className="text-center py-12">
          <Icon name="Shield" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No team yet</h3>
          <p className="text-muted-foreground mb-4">
            You're not part of any team. Create a new team or accept an invitation to get started.
          </p>
          <div className="flex items-center justify-center space-x-3">
            <Button
              variant="default"
              onClick={() => setIsTeamBuilderOpen(true)}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
            >
              Create Team
            </Button>
            <Button
              variant="outline"
              onClick={() => setActiveTab('find-teammates')}
              iconName="Users"
              iconPosition="left"
              iconSize={16}
            >
              Find Teammates
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="student" userName={currentUser?.name} notifications={3} />
      <main className="container mx-auto px-4 py-6">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Team Formation Hub</h1>
          <p className="text-muted-foreground">
            Connect with talented students across different roles to form interdisciplinary teams for smart city projects.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-border">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.count > 0 && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {activeTab === 'find-teammates' && renderFindTeammates()}
          {activeTab === 'invitations' && renderInvitations()}
          {activeTab === 'current-team' && renderCurrentTeam()}
        </div>
      </main>
      {/* Team Builder Modal */}
      <TeamBuilderModal
        isOpen={isTeamBuilderOpen}
        onClose={() => setIsTeamBuilderOpen(false)}
        onCreateTeam={handleCreateTeam}
        problemStatements={mockProblemStatements}
      />
    </div>
  );
};

export default TeamFormationHub;